import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  setDoc, // Used for explicit ID creation
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase'; // Adjust path as necessary
import {
  AppAction,
  KanbanTask,
  TaskStatus,
  BoardConfig,
  PriorityTier,
  TemplateType,
  TaskSource,
  Role,
  PermissionFlags,
  DataScope,
  TaskScope
} from '@/types';
import { useConsumption } from './useConsumption';
import { useRecurrence } from './useRecurrence';

interface AuditLogEntry {
  id: string;
  timestamp: number;
  actorId: string;
  actionType: 'MOVE' | 'CREATE' | 'DELETE' | 'CONSUME' | 'MIGRATE' | 'UPDATE' | 'RECUR';
  taskTitle: string;
  fromStatus?: TaskStatus;
  toStatus?: TaskStatus;
  fromBoardId?: string;
  toBoardId?: string;
  isOfflineSync: boolean;
  metadata?: Record<string, any>;
  scope: TaskScope; 
}

interface UseTaskStateReturn {
  tasks: KanbanTask[];
  logs: AuditLogEntry[];
  pendingSyncCount: number;
  isOnline: boolean;
  createTask: (
    title: string,
    templateType: TemplateType,
    boardId: string,
    tags?: string[],
    scope?: TaskScope,
    recurrence?: KanbanTask['recurrence'],
    dueDate?: string,
    description?: string,
    priorityTier?: PriorityTier,
    cost?: number,
    consumption?: KanbanTask['consumption']
  ) => void;
  moveTask: (taskId: string, newStatus: TaskStatus, destinationBoard?: BoardConfig) => void;
  consumeQuantity: (taskId: string, amount: number) => void;
  reconcileOfflineChanges: (serverTasks: KanbanTask[]) => void;
}

/**
 * 🛰️ MASTER TASK STATE HOOK v2.0
 * Orchestrates partitioned state for Personal and Shared task streams.
 */
export const useTaskState = (
  initialTasks: KanbanTask[],         // Argument 1
  currentUserId: string,              // Argument 2
  dispatch: React.Dispatch<AppAction>,// Argument 3
  currentHouseholdId: string | null,  // Argument 4
  activeDataScope: DataScope,         // Argument 5 (User's preferred scope)
  userPermissions?: PermissionFlags 
): UseTaskStateReturn => {
  const [tasks, setTasks] = useState<KanbanTask[]>(initialTasks);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [pendingSync, setPendingSync] = useState<KanbanTask[]>([]);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  // 🛡️ Initialize child hooks with the current scope context, passing userPermissions
  const { processConsumptionOnArchive } = useConsumption(currentUserId, currentHouseholdId, dispatch, activeDataScope, userPermissions);
  const { processTaskRecurrence } = useRecurrence(currentUserId, currentHouseholdId, dispatch, activeDataScope, userPermissions);

  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const createLog = useCallback((entry: Omit<AuditLogEntry, 'id' | 'timestamp' | 'isOfflineSync'>) => {
    const newLog: AuditLogEntry = {
      ...entry,
      id: `log-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
      isOfflineSync: !navigator.onLine
    };
    setLogs(prev => [newLog, ...prev]);
  }, []);

  const getTaskCollectionRef = useCallback((taskScope: TaskScope) => {
    if (taskScope === 'personal' || !currentHouseholdId) {
      return collection(db, 'users', currentUserId, 'personalTasks');
    }
    return collection(db, 'households', currentHouseholdId, 'tasks');
  }, [currentUserId, currentHouseholdId]);

  // --- 🏗️ SCOPE-AWARE CREATION LOGIC ---
  const createTask = useCallback(async (
    title: string,
    template: TemplateType,
    boardId: string,
    tags: string[] = [],
    scope: TaskScope = 'personal', // Default scope if not provided
    recurrence: KanbanTask['recurrence'] = { isEnabled: false, interval: 0, frequency: null, completionTrigger: false, lastResetDate: undefined },
    dueDate: string | undefined = undefined,
    description: string | undefined = undefined,
    priorityTier: PriorityTier = 'none',
    cost: number | undefined = undefined,
    consumption: KanbanTask['consumption'] = undefined
  ) => {
    // 🛡️ Privacy Gate: Force 'personal' if no household is active for non-personal scopes
    const effectiveScope: TaskScope = (!currentHouseholdId && scope !== 'personal') ? 'personal' : scope;

    // 🛡️ Permission Check for Shared Scope
    if (effectiveScope === 'household' && userPermissions) {
      if ((template === TemplateType.ACTION || template === TemplateType.MAINTENANCE) && !userPermissions.canManageTasks) {
        console.error("Unauthorized: User lacks permission to create household tasks.");
        return;
      }
      if (template === TemplateType.GROCERY && !userPermissions.canManageGroceries) {
        console.error("Unauthorized: User lacks permission to create household grocery tasks.");
        return;
      }
    }

    const isHistorical = dueDate ? new Date(dueDate).getTime() < Date.now() && template !== TemplateType.MAINTENANCE : false;

    const taskCollectionRef = getTaskCollectionRef(effectiveScope);
    const newTaskId = doc(taskCollectionRef).id;

    const newTask: KanbanTask = {
      id: newTaskId,
      title: title.trim(),
      description: description || '',
      status: (isHistorical ? 'history' : 'todo') as TaskStatus,
      templateType: template,
      boardId: boardId,
      lastBoardId: null,
      source: 'manual', // Default source
      tags: tags,
      ownerId: currentUserId, // Creator is the owner
      assignedTo: currentUserId, // Assigned to creator by default
      createdAt: new Date().toISOString(),
      visibilityRole: Role.MEMBER,
      isLocked: false,
      isBaseline: false,
      completedAt: isHistorical ? (dueDate || new Date().toISOString()) : undefined,
      localChangeTimestamp: Date.now(),
      scope: effectiveScope,
      priorityTier: priorityTier,
      energyRequired: 3, // Default energy
      focusLevel: 'shallow',
      contributionPoints: 10,
      lastModifiedBy: currentUserId,
      isDeleted: false,
      subtasks: [],
      consumables: [],
      toolsRequired: [],
      documents: [],
      estimatedTime: 15,
      actualTime: 0,
      consumption: template === TemplateType.GROCERY ? {
        quantity: 1, remainingQuantity: 1, unit: 'unit', stockLevel: 1, lastAddedHistory: undefined, dayBetweenRecurrence: undefined, averageCycle: undefined,
        cost: cost || 0, // Include cost with a default of 0
        ...(consumption || {})
      } : undefined, // consumption is optional in KanbanTask
      recurrence: template === TemplateType.MAINTENANCE ? { ...recurrence, isEnabled: recurrence?.isEnabled || true, lastResetDate: recurrence?.lastResetDate || new Date().toISOString() } : recurrence,
      dueDate: dueDate
    };

    try {
      await setDoc(doc(taskCollectionRef, newTask.id), {
        ...newTask,
        createdAt: serverTimestamp(),
        localChangeTimestamp: serverTimestamp(),
      });
      dispatch({ type: 'ADD_TASK', payload: newTask });
    } catch (e) {
      console.error("Error creating task:", e);
    }
  }, [currentUserId, currentHouseholdId, userPermissions, getTaskCollectionRef, dispatch]);

  // --- 🔄 SCOPE-AWARE TRANSITION ENGINE ---
  const moveTask = useCallback(async (taskId: string, newStatus: TaskStatus, destinationBoard?: BoardConfig) => {
    const taskToMove = tasks.find(t => t.id === taskId);
    if (!taskToMove) return;

    // 🛡️ Permission Guard for Household Mutations
    if (taskToMove.scope === 'household' && userPermissions && !userPermissions.canManageTasks) {
      console.error("Unauthorized: User lacks permission to move household tasks.");
      return;
    }

    const taskRef = doc(getTaskCollectionRef(taskToMove.scope), taskId);

    const isMigration = destinationBoard && destinationBoard.id !== taskToMove.boardId;
    const oldStatus = taskToMove.status;
    const oldBoardId = taskToMove.boardId;
    let updatedTask: KanbanTask = { ...taskToMove };

    if (isMigration) {
      updatedTask.boardId = destinationBoard!.id;
      updatedTask.templateType = destinationBoard!.templateType;
      updatedTask.status = destinationBoard!.allowedStatuses[0] || 'todo';
      createLog({
        actorId: currentUserId, actionType: 'MIGRATE', taskTitle: taskToMove.title,
        fromBoardId: oldBoardId, toBoardId: destinationBoard!.id, scope: taskToMove.scope
      });
    } else {
      updatedTask.status = newStatus;
    }

    if (newStatus === 'history' && updatedTask.templateType === TemplateType.GROCERY) {
      updatedTask = processConsumptionOnArchive(updatedTask, updatedTask.scope);
      createLog({
        actorId: currentUserId, actionType: 'CONSUME', taskTitle: taskToMove.title,
        fromStatus: oldStatus, toStatus: 'history', scope: updatedTask.scope
      });
    } else if ((newStatus === 'finalized' || newStatus === 'done') && updatedTask.recurrence?.isEnabled && oldStatus !== newStatus) { // Removed 'todo' from here, recurrence is for completion
        if (['todo', 'in_progress'].includes(oldStatus)) {
            const spawnedRecurrenceTask = await processTaskRecurrence(updatedTask);
            if (spawnedRecurrenceTask) {
                dispatch({ type: 'ADD_TASK', payload: spawnedRecurrenceTask });
                createLog({
                    actorId: currentUserId, actionType: 'RECUR', taskTitle: updatedTask.title,
                    scope: updatedTask.scope, metadata: { nextDue: spawnedRecurrenceTask.dueDate }
                });
            }
        }
        updatedTask.status = newStatus;
        updatedTask.completedAt = new Date().toISOString();
    } else if (newStatus === 'history' || newStatus === 'finalized' || newStatus === 'done') { // For other finalized/history states
        updatedTask.completedAt = new Date().toISOString();
        createLog({
          actorId: currentUserId, actionType: 'MOVE', taskTitle: taskToMove.title,
          fromStatus: oldStatus, toStatus: newStatus, scope: updatedTask.scope
        });
    } else { // Standard status change
        createLog({
          actorId: currentUserId, actionType: 'MOVE', taskTitle: taskToMove.title,
          fromStatus: oldStatus, toStatus: newStatus, scope: updatedTask.scope
        });
    }

    updatedTask.localChangeTimestamp = Date.now();
    updatedTask.lastModifiedBy = currentUserId;

    try {
      await updateDoc(taskRef, {
        status: updatedTask.status,
        boardId: updatedTask.boardId,
        templateType: updatedTask.templateType,
        completedAt: updatedTask.completedAt,
        localChangeTimestamp: serverTimestamp(),
        consumption: updatedTask.consumption,
        recurrence: updatedTask.recurrence,
        lastModifiedBy: updatedTask.lastModifiedBy,
        // Ensure other fields that might change on move are included in updates if necessary
      });

      dispatch({ type: 'UPDATE_TASK', payload: { id: taskId, updates: updatedTask } });
    } catch (e) {
      console.error("Error moving task:", e);
    }

  }, [tasks, currentUserId, userPermissions, getTaskCollectionRef, processConsumptionOnArchive, processTaskRecurrence, createLog, dispatch]);


  const consumeQuantity = useCallback(async (taskId: string, amount: number) => {
    const taskToConsume = tasks.find(t => t.id === taskId);
    if (!taskToConsume || !taskToConsume.consumption) return;

    // 🛡️ Permission Guard
    if (taskToConsume.scope === 'household' && userPermissions && !userPermissions.canManageGroceries) {
      console.error("Unauthorized: User lacks permission to manage household groceries.");
      return;
    }

    const newRemaining = Math.max(0, taskToConsume.consumption.remainingQuantity - amount);
    const newStockLevel = taskToConsume.consumption.quantity > 0 ? newRemaining / taskToConsume.consumption.quantity : 0;

    const updatedConsumption = {
        ...taskToConsume.consumption,
        remainingQuantity: newRemaining,
        stockLevel: newStockLevel
    };

    const taskRef = doc(getTaskCollectionRef(taskToConsume.scope), taskId);

    try {
        await updateDoc(taskRef, {
            consumption: updatedConsumption,
            localChangeTimestamp: serverTimestamp(),
            lastModifiedBy: currentUserId
        });

        createLog({
            actorId: currentUserId, actionType: 'UPDATE', taskTitle: taskToConsume.title,
            scope: taskToConsume.scope, metadata: { remaining: newRemaining, unit: taskToConsume.consumption.unit }
        });

        if (newRemaining === 0) {
            await moveTask(taskId, 'history'); // Await the moveTask
        }

        dispatch({ type: 'UPDATE_TASK', payload: { id: taskId, updates: { consumption: updatedConsumption, lastModifiedBy: currentUserId } } });

    } catch (e) {
        console.error("Error consuming quantity:", e);
    }
  }, [tasks, currentUserId, userPermissions, getTaskCollectionRef, moveTask, createLog, dispatch]);


  const reconcileOfflineChanges = useCallback((serverTasks: KanbanTask[]) => {
    const reconciled = serverTasks.map((serverTask: KanbanTask) => {
      const localUpdate = pendingSync.find(p => p.id === serverTask.id);
      if (localUpdate) {
        const localTime = localUpdate.localChangeTimestamp || 0;
        const serverTime = serverTask.localChangeTimestamp || 0;

        if (localTime > serverTime) {
          return { ...serverTask, ...localUpdate, localChangeTimestamp: localTime };
        }
      }
      return serverTask;
    });

    setTasks(reconciled);
    setPendingSync([]);
    dispatch({ type: 'SET_TASKS', payload: reconciled });
  }, [pendingSync, dispatch]);


  return {
    tasks, logs, moveTask, createTask, consumeQuantity, isOnline,
    pendingSyncCount: pendingSync.length, reconcileOfflineChanges
  };
};